import { PlaceItem } from '../types/explore';
import { apiClient } from '../utils/apiClient';

class SavedPlacesService {
  /**
   * Save a place to user's saved places
   * @param userId - The user ID
   * @param place - The place to save
   * @returns Promise with success response
   */
  async savePlace(userId: string, place: PlaceItem): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/api/saved-places/save', { userId, place });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save place');
      }

      return data.data;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  /**
   * Remove a place from user's saved places
   * @param userId - The user ID
   * @param placeId - The place ID to unsave
   * @returns Promise with success response
   */
  async unsavePlace(userId: string, placeId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/api/saved-places/unsave', { userId, placeId });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to unsave place');
      }

      return data.data;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  /**
   * Get user's saved places
   * @param userId - The user ID
   * @returns Promise with array of saved places
   */
  async getSavedPlaces(userId: string): Promise<PlaceItem[]> {
    try {
      const response = await apiClient.get(`/api/saved-places/${userId}`);
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get saved places');
      }

      return data.data;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  /**
   * Check if a place is saved by the user
   * @param userId - The user ID
   * @param placeId - The place ID to check
   * @returns Promise with boolean indicating if place is saved
   */
  async isPlaceSaved(userId: string, placeId: string): Promise<boolean> {
    try {
      const response = await apiClient.get(`/api/saved-places/check/${userId}/${placeId}`);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check if place is saved');
      }

      return data.data.isSaved;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }
}

export const savedPlacesService = new SavedPlacesService();
